<template>
  <v-app-bar
    app
    flat
    height="72"
    class="navbar-gradient"
  >
    <v-container fluid class="d-flex align-center justify-space-between px-4">
      <!-- Logo -->
      <div class="d-flex align-center cursor-pointer logo-wrapper" @click="onBack">
          <v-img
            :src="logo"
            contain
            :width="$vuetify.breakpoint.smAndDown ? 160 : 220"
            :height="$vuetify.breakpoint.smAndDown ? 52 : 72"
            class="logo-img"
          />
      </div>

      <!-- Desktop Search -->
      <v-text-field
        v-if="isAuthenticated && profile.type !== 'Company' && profile.type !== 'Admin'"
        v-model="searchKey"
        class="search-bar hidden-sm-and-down"
        hide-details
        placeholder="Search..."
        prepend-inner-icon="mdi-magnify"
        solo-inverted
        flat
        dense
        rounded
        @keyup.enter="onSearch"
      />

      <!-- Desktop Tabs -->
      <v-tabs
        v-if="isAuthenticated && profile.type !== 'Company' && profile.type !== 'Admin'"
        :value="selectedMenu"
        background-color="transparent"
        class="hidden-sm-and-down nav-tabs"
        :class="{ 'nav-tabs-no-active': isSpecialPage }"
        color="primary"
        hide-slider
      >
        <v-tab
          v-for="(item, i) in menus"
          :key="i"
          @click="onSelected(i)" 
          :class="{ 
            'force-inactive': isSpecialPage,
            'active-tab': !isSpecialPage && isRouteActive(item)
          }"
        >
          <v-badge
            v-if="item.title === 'Notifications'"
            :content="notificationCount"
            :value="notificationCount > 0"
            color="red"
            overlap
            offset-x="10"
            offset-y="10"
          >
            <v-icon left color="#ffff">{{ item.icon }}</v-icon>
          </v-badge>
          <v-icon v-else left color="#ffff">{{ item.icon }}</v-icon>
          <span class="tab-label">{{ item.title }}</span>
        </v-tab>
      </v-tabs>

      <!-- Auth Buttons -->
      <div v-if="!isAuthenticated && !loading" class="hidden-sm-and-down d-flex align-center">
        <v-btn text class="mr-2 auth-btn" @click="onLogin">Login</v-btn>
        <v-btn color="primary" rounded @click="onRegister">Get Started</v-btn>
      </div>

      <!-- Profile (Desktop) -->
      <v-menu
        v-if="isAuthenticated"
        offset-y
        transition="slide-y-transition"
        :close-on-content-click="false"
        class="profile-menu"
        content-class="profile-menu-content"
        min-width="200"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="profile-btn hidden-sm-and-down"
            :class="{ 'profile-active': isSpecialPage }"
          >
            <v-avatar 
              size="36" 
              class="profile-avatar"
              :class="{ 'avatar-active': isSpecialPage }"
            >
              <v-img
                :src="profile.thumbnail || profile.image || placeholderImage"
                class="profile-img"
              />
            </v-avatar>
            <v-icon small class="dropdown-icon">mdi-chevron-down</v-icon>
          </v-btn>
        </template>

        <v-card class="profile-card">
          <div class="profile-header">
            <v-avatar size="48" class="header-avatar">
              <v-img
                :src="profile.thumbnail || profile.image || placeholderImage"
              />
            </v-avatar>
            <div class="profile-info">
              <div class="profile-name">{{ profile.name || profile.email }}</div>
              <div class="profile-email">{{ profile.type }}</div>
            </div>
          </div>

          <v-divider class="profile-divider" />

          <v-list class="menu-list">
            <v-list-item
              v-for="(item, i) in profile.type === 'Company' ? companydropmenus : dropmenus"
              :key="i"
              @click="onSelectedMenu(i)"
              class="menu-item"
              :class="{ 
                'menu-item-active': (isProfilePage && item.title === 'Profile') || (isSettingsPage && item.title === 'Settings')
              }"
            >
              <v-list-item-icon class="menu-icon">
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title 
                  class="menu-title"
                  :class="{ 
                    'menu-title-active': (isProfilePage && item.title === 'Profile') || (isSettingsPage && item.title === 'Settings')
                  }"
                >
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>

      <!-- Mobile Menu Icon -->
      <v-app-bar-nav-icon 
        class="hidden-md-and-up mobile-menu-icon" 
        @click="showMobileMenu = !showMobileMenu"
        color="#ffff"
      />

      <!-- Mobile Drawer -->
      <v-navigation-drawer
        v-model="showMobileMenu"
        temporary
        fixed
        right
        class="mobile-drawer"
        :width="$vuetify.breakpoint.xsOnly ? '100%' : '380px'"
        overlay-opacity="0.3"
        overlay-color="#333"
      >
        <div class="drawer-container">
          <!-- Header with close button -->
          <div class="drawer-header">
            <v-btn icon @click="showMobileMenu = false" class="close-btn">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Search Bar -->
          <div class="search-container" v-if="isAuthenticated && profile.type !== 'Company'">
            <v-text-field
              v-model="searchKey"
              hide-details
              solo
              flat
              rounded
              placeholder="Search..."
              prepend-inner-icon="mdi-magnify"
              class="mobile-search"
              @keyup.enter="onSearchMobile"
            >
              <template v-slot:prepend-inner>
                <v-icon color="primary">mdi-magnify</v-icon>
              </template>
            </v-text-field>
          </div>

          <!-- Navigation Items -->
          <v-list class="nav-items">
            <v-list-item
              v-for="(item, i) in menus"
              :key="i"
              @click="onMobileNavClick(i)"
              :class="{ 'active-item': isRouteActive(item) }"
              class="nav-item"
            >
              <v-list-item-icon class="item-icon">
                <v-badge
                  v-if="item.title === 'Notifications'"
                  :content="notificationCount"
                  :value="notificationCount > 0"
                  color="red"
                  overlap
                  offset-x="8"
                  offset-y="8"
                >
                  <v-icon :color="isRouteActive(item) ? 'primary' : 'textSecondary'">
                    {{ item.icon }}
                  </v-icon>
                </v-badge>
                <v-icon v-else :color="isRouteActive(item) ? 'primary' : 'textSecondary'">
                  {{ item.icon }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="item-title">
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item-content>
              <v-icon small class="chevron">mdi-chevron-right</v-icon>
            </v-list-item>
          </v-list>

          <!-- Divider -->
          <v-divider class="divider" />

          <!-- Profile/Auth Options -->
          <div class="action-section">
            <template v-if="isAuthenticated">
              <v-list class="profile-options">
                <v-list-item
                  v-for="(item, i) in profile.type === 'Company' ? companydropmenus : dropmenus"
                  :key="'drop-' + i"
                  @click="onSelectedMenu(i)"
                  class="profile-item"
                  :class="{ 
                    'profile-item-active': (isProfilePage && item.title === 'Profile') || (isSettingsPage && item.title === 'Settings')
                  }"
                >
                  <v-list-item-icon>
                    <v-icon>{{ item.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title 
                      :class="{ 
                        'profile-title-active': (isProfilePage && item.title === 'Profile') || (isSettingsPage && item.title === 'Settings')
                      }"
                    >
                      {{ item.title }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>
            <template v-else>
              <v-btn
                block
                text
                class="login-btn"
                @click="onLogin"
              >
                Login
              </v-btn>
              <v-btn
                block
                color="primary"
                rounded
                depressed
                class="register-btn"
                @click="onRegister"
              >
                Get Started
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </template>
          </div>
        </div>
      </v-navigation-drawer>
    </v-container>
  </v-app-bar>
</template>


<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  data() {
    return {
      searchKey: "",
      showMobileMenu: false,
      placeholderImage: require("../assets/default.jpg"),
      dropmenus: [{ title: "Profile" ,icon: "mdi mdi-account"}, { title: "Settings", icon: "mdi mdi-cog"}, { title: "Logout", icon: "mdi mdi-logout" }],
      companydropmenus: [{ title: "Profile", icon: "mdi mdi-account"}, { title: "Logout", icon: "mdi mdi-logout" }],
      menus: [
        { title: "Home", router: "home", icon: "mdi-home", path: "/" },
        { title: "Needs", router: "agency-need-feeds", icon: "mdi-hand-heart", path: "/needs/feeds" },
        { title: "Chat", router: "chat", icon: "mdi-chat", path: "/chat" },
        { title: "Notifications", router: "notifications", icon: "mdi-bell", path: "/notifications" }
      ],
      // Child routes that should map to parent menu items
      routeMapping: {
        'agency-need-details': 'agency-need-feeds',
        'agency-need-discover': 'agency-need-feeds',
        'agency-need-notification': 'agency-need-feeds',
        'chat-details': 'chat',
        'mobile-chat': 'chat',
        'mobile-chat-room': 'chat'
      }
    };
  },
  computed: {
    ...mapGetters({
      isAuthenticated: "auth/isAuthenticated",
      notificationCount: "notification/getUnreadCount"
    }),
    ...mapState("auth", {
      profile: "profile",
      profileType: "type",
      loading: "loading"
    }),
    logo() {
      return require(`@/assets/${process.env.VUE_APP_LOGO}`);
    },
    isProfilePage() {
      const profileRoutes = [
        'volunteer-profile',
        'volunteer-profile-details',
        'volunteer-profile-events', 
        'volunteer-profile-about',
        'volunteer-profile-needs',
        'volunteer-profile-agencies',
        'agency-profile',
        'agency-profile-details',
        'agency-profile-events',
        'agency-profile-about', 
        'agency-profile-needs',
        'agency-profile-volunteers',
        'company-profile',
        'company-profile-details',
        'company-profile-events',
        'company-profile-about',
        'company-profile-needs',
        'company-profile-agencies'
      ];
      
      return profileRoutes.includes(this.$route.name);
    },
    isSettingsPage() {
      return this.$route.name === 'settings';
    },
    isSpecialPage() {
      return this.isProfilePage || this.isSettingsPage;
    },
    selectedMenu() {
      // If we're on a special page (profile, settings, etc.), return null to deactivate all tabs
      if (this.isSpecialPage) {
        return null;
      }
      
      // Get the current route name
      const currentRoute = this.$route.name;
      
      // Check if current route has a mapping
      const mappedRoute = this.routeMapping[currentRoute];
      if (mappedRoute) {
        // Find the index of the mapped route
        const index = this.menus.findIndex(menu => menu.router === mappedRoute);
        return index >= 0 ? index : null;
      }
      
      // Otherwise, find the matching menu index
      const index = this.menus.findIndex(menu => menu.router === currentRoute);
      return index >= 0 ? index : null;
    }
  },
  methods: {
    ...mapActions({
      onLogout: "auth/onLogout",
      fetchProfile: "auth/fetchProfile"
    }),
    onSearch() {
      if (!this.searchKey.trim()) return;
      this.$router.push({ name: "search", query: { q: this.searchKey } });
      this.showMobileMenu = false;
    },
    onSearchMobile() {
      this.onSearch();
    },
    isRouteActive(item) {
      // Don't highlight if on special page (profile, settings)
      if (this.isSpecialPage) {
        return false;
      }
      
      const currentRoute = this.$route.name;
      
      // Check if current route matches the item's router
      if (currentRoute === item.router) {
        return true;
      }
      
      // Check if current route has a mapping to this item
      const mappedRoute = this.routeMapping[currentRoute];
      if (mappedRoute && mappedRoute === item.router) {
        return true;
      }
      
      return false;
    },
    onSelected(index) {
      this.$router.push({ name: this.menus[index].router });
    },
    onMobileNavClick(index) {
      this.onSelected(index);
      this.showMobileMenu = false;
    },
    onSelectedMenu(index) {
      const menuList = this.profile.type === "Company" ? this.companydropmenus : this.dropmenus;
      const title = menuList[index].title;

      if (title === "Logout") {
        this.onLogout().then(() => this.$router.push({ name: "login" }));
      } else if (title === "Profile") {
        this.$router.push({ name: `${this.profileType.toLowerCase()}-profile` });
      } else if (title === "Settings") {
        this.$router.push({ name: "settings" });
      }

      this.showMobileMenu = false;
    },
    onBack() {
      this.$router.push({ name: "home" });
    },
    onLogin() {
      this.$router.push({ name: "login" });
      this.showMobileMenu = false;
    },
    onRegister() {
      this.$router.push({ name: "user-register" });
      this.showMobileMenu = false;
    }
  },
  created() {
    this.fetchProfile();
  }
};
</script>

<style scoped>
.navbar-gradient {
  background: linear-gradient(135deg, #084468 0%, #1da1f2 50%, #3b7ca1 100%) !important;
  color: white;
  box-shadow: none;
  z-index: 195;
  position: fixed;
}

/* Search Bar Styling */
.search-bar {
  max-width: 280px;
  margin: 0 16px;
}

.search-bar >>> .v-input__control {
  background-color: white !important;
  border-radius: 24px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.search-bar >>> .v-input__slot {
  background-color: white !important;
  border-radius: 24px !important;
}

.search-bar >>> input {
  color: #333 !important;
  font-size: 14px !important;
}

.search-bar >>> input::placeholder {
  color: #666 !important;
  opacity: 0.7;
}

.search-bar >>> .v-icon {
  color: #666 !important;
}

.search-bar >>> .v-input__prepend-inner {
  margin-top: 8px !important;
}

/* Focus state for search */
.search-bar >>> .v-input--is-focused .v-input__slot {
  background-color: white !important;
  box-shadow: 0 0 0 2px rgba(43, 176, 102, 0.3) !important;
}

.search-bar >>> .v-input--is-focused input {
  color: #333 !important;
}

.search-bar >>> .v-input--is-focused .v-icon {
  color: #084468 !important;
}

.nav-tabs {
  max-width: 500px;
  margin-left: 16px;
}

.tab-label {
  color: #ffff !important;
  text-transform: capitalize;
  font-size: 14px;
}

/* FIX: Active tab styling */
.active-tab {
  background-color: rgba(255, 255, 255, 0.15) !important;
  border-bottom: 3px solid #2BB066 !important;
  position: relative;
}

.active-tab .tab-label {
  color: #ffffff !important;
  font-weight: 600 !important;
}

.active-tab .v-icon {
  color: #ffffff !important;
}


/* Ensure tab hover effect */
.v-tab:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

/* Force no active state when on profile pages */
.nav-tabs-no-active >>> .v-tab--active {
  background-color: transparent !important;
  border-bottom: none !important;
  opacity: 0.7 !important;
}

.nav-tabs-no-active >>> .v-tab--active .tab-label {
  color: rgba(255, 255, 255, 0.7) !important;
  font-weight: normal !important;
}

.nav-tabs-no-active >>> .v-tab--active .v-icon {
  color: rgba(255, 255, 255, 0.7) !important;
}

.nav-tabs-no-active >>> .v-tabs-slider {
  display: none !important;
}

.force-inactive {
  opacity: 0.7 !important;
}

.force-inactive:hover {
  opacity: 0.9 !important;
}

/* Stronger overrides to prevent any active state on profile pages */
.nav-tabs-no-active >>> .v-tab:first-child.v-tab--active {
  background-color: transparent !important;
  border-bottom: none !important;
  box-shadow: none !important;
}

.nav-tabs-no-active >>> .v-tab.v-tab--active .tab-label {
  color: rgba(255, 255, 255, 0.7) !important;
  font-weight: normal !important;
}

/* Desktop Auth Buttons */
.auth-btn {
  color: #ffff !important;
}

/* Mobile Menu Icon */
.mobile-menu-icon {
  color: #ffff !important;
}

/* Profile Button */
.profile-btn {
  color: #ffff !important;
}

.mobile-drawer {
  height: 100vh !important;
  top: 0 !important;
  z-index: 9999 !important;
}

/* Mobile Auth Buttons */
.mobile-auth-btn {
  color: #084468 !important;
}

/* Mobile Search Styling */
.mobile-drawer .v-text-field {
  background-color: #f5f5f5 !important;
  border-radius: 24px !important;
}

.mobile-drawer .v-text-field >>> .v-input__control {
  background-color: #f5f5f5 !important;
  border-radius: 24px !important;
}

.mobile-drawer .v-text-field >>> input {
  color: #333 !important;
}

.mobile-drawer .v-text-field >>> input::placeholder {
  color: #666 !important;
}

.mobile-drawer .v-text-field >>> .v-icon {
  color: #666 !important;
}

/* Ensure all icons in mobile drawer are properly colored */
.mobile-drawer .v-icon {
  color: #333 !important;
}

/* Override for mobile menu icons to be dark in drawer */
.mobile-drawer .v-list-item-icon .v-icon {
  color: #333 !important;
}

/* Keep active mobile item icon highlighted */
.active-item .v-icon {
  color: #1976D2 !important;
}

/* Logo */
.logo-wrapper {
  height: 80px;
}

.logo-img {
  object-fit: contain;
}

/* Responsive adjustments */
@media (max-width: 959px) {
  .search-bar {
    max-width: 200px;
    margin: 0 8px;
  }
}

@media (max-width: 599px) {
  .logo-wrapper {
    height: 60px;
  }
  
  .navbar-gradient {
    height: 64px !important;
  }
}

/* Fix for Vuetify text field outline and background */
.search-bar >>> .v-text-field--solo > .v-input__control > .v-input__slot {
  background-color: white !important;
  border-radius: 24px !important;
}

.search-bar >>> .v-text-field--solo-inverted > .v-input__control > .v-input__slot {
  background-color: white !important;
  border-radius: 24px !important;
}

/* Ensure the input field stays white on focus */
.search-bar >>> .v-text-field--solo.v-input--is-focused > .v-input__control > .v-input__slot {
  background-color: white !important;
}

/* Mobile Responsive nav bar styles */
.mobile-drawer {
  z-index: 1001 !important;
  box-shadow: 0 0 24px rgba(0, 0, 0, 0.3) !important;
}

.mobile-drawer .drawer-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
}

.mobile-drawer .drawer-header {
  display: flex;
  justify-content: flex-end;
  padding: 8px 0 16px;
}

.mobile-drawer .drawer-header .close-btn {
  margin-right: -8px;
  transition: transform 0.2s ease;
}

.mobile-drawer .drawer-header .close-btn:hover {
  transform: rotate(90deg);
}

.mobile-drawer .drawer-header .close-btn .v-icon {
  font-size: 24px;
  color: #757575;
}

.mobile-drawer .search-container {
  padding: 0 8px 16px;
}

.mobile-drawer .search-container .mobile-search >>> .v-input__slot {
  background: rgba(0, 0, 0, 0.05) !important;
  border-radius: 24px !important;
  box-shadow: none !important;
  padding: 0 12px !important;
  min-height: 48px !important;
}

.mobile-drawer .search-container .mobile-search >>> input {
  color: #212121 !important;
  font-size: 16px;
  padding-top: 12px !important;
}

.mobile-drawer .search-container .mobile-search >>> .v-input__prepend-inner {
  margin-top: 12px !important;
  margin-right: 8px !important;
}

.mobile-drawer .nav-items {
  padding: 0;
}
.mobile-drawer .action-section .profile-options{
    padding: 0;
    margin-bottom: 328px;
}

.mobile-drawer .nav-items .nav-item {
  border-radius: 8px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
  padding: 0 12px;
  height: 56px;
}

.mobile-drawer .nav-items .nav-item .item-icon {
  min-width: 40px;
  margin-right: 12px;
}

.mobile-drawer .nav-items .nav-item .item-icon .v-icon {
  font-size: 24px;
}

.mobile-drawer .nav-items .nav-item .item-title {
  font-size: 16px;
  font-weight: 500;
  color: #212121;
}

.mobile-drawer .nav-items .nav-item .chevron {
  color: #757575;
  opacity: 0.7;
}

.mobile-drawer .nav-items .nav-item:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.mobile-drawer .nav-items .nav-item.active-item {
  background-color: rgba(43, 176, 102, 0.15) !important;
  border-left: 4px solid #2BB066;
}

.mobile-drawer .nav-items .nav-item.active-item .item-title {
  color: #1976D2;
  font-weight: 600;
}

.mobile-drawer .nav-items .nav-item.active-item .v-icon {
  color: #1976D2 !important;
}

.mobile-drawer .divider {
  margin: 16px 0;
  border-color: #e0e0e0 !important;
  opacity: 0.5;
}

.mobile-drawer .action-section {
  margin-top: auto;
}

.mobile-drawer .action-section .login-btn {
  color: #1976D2 !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  height: 48px;
  margin-bottom: 12px;
  text-transform: none;
  font-size: 16px;
}

.mobile-drawer .action-section .register-btn {
  font-weight: 500;
  letter-spacing: 0.5px;
  height: 48px;
  text-transform: none;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.2);
  transition: all 0.3s ease;
}

.mobile-drawer .action-section .register-btn:hover {
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

.mobile-drawer .action-section .register-btn .v-icon {
  transition: transform 0.3s ease;
}

.mobile-drawer .action-section .register-btn:hover .v-icon {
  transform: translateX(2px);
}

.mobile-drawer .action-section .profile-options {
  padding: 0;
}

.mobile-drawer .action-section .profile-options .profile-item {
  padding: 0 12px;
  height: 48px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.mobile-drawer .action-section .profile-options .profile-item .v-icon {
  color: #757575;
  font-size: 20px;
}

.mobile-drawer .action-section .profile-options .profile-item:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
}

@media (max-width: 600px) {
  .mobile-drawer .drawer-container {
    padding: 12px;
  }

  .mobile-drawer .search-container {
    padding: 0 4px 12px;
  }

  .mobile-drawer .nav-items .nav-item {
    height: 52px;
  }

  .mobile-drawer .nav-items .nav-item .item-icon .v-icon {
    font-size: 22px;
  }

  .mobile-drawer .nav-items .nav-item .item-title {
    font-size: 15px;
  }

  .mobile-drawer .action-section .login-btn,
  .mobile-drawer .action-section .register-btn {
    height: 44px;
    font-size: 15px;
  }
}

/* Profile menu */
.profile-menu {
  margin-left: 12px;
}
.profile-menu .profile-btn {
  transition: all 0.3s ease;
  padding: 4px;
}
.profile-menu .profile-btn:hover {
  transform: translateY(-2px);
}
.profile-menu .profile-btn:hover .dropdown-icon {
  color: #1976D2;
}
.profile-menu .profile-btn .profile-avatar {
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}
.profile-menu .profile-btn:hover .profile-avatar {
  border-color: rgba(255, 255, 255, 0.6);
}
.profile-menu .profile-btn .profile-img {
  transition: all 0.3s ease;
}
.profile-menu .profile-btn .dropdown-icon {
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.3s ease;
  margin-left: 2px;
}

.profile-menu-content {
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
  margin-top: 8px !important;
}

.profile-card {
  padding: 16px 0;
}
.profile-card .profile-header {
  display: flex;
  align-items: center;
  padding: 0 16px 12px;
}
.profile-card .profile-header .header-avatar {
  margin-right: 12px;
  border: 2px solid #e0e0e0;
}
.profile-card .profile-header .profile-info {
  overflow: hidden;
}
.profile-card .profile-header .profile-info .profile-name {
  font-weight: 600;
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #212121;
}
.profile-card .profile-header .profile-info .profile-email {
  font-size: 13px;
  color: #757575;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.profile-card .profile-divider {
  margin: 8px 0;
  border-color: #e0e0e0 !important;
  opacity: 0.5;
}
.profile-card .menu-list {
  padding: 0;
}
.profile-card .menu-list .menu-item {
  padding: 8px 16px;
  min-height: 48px;
  transition: all 0.2s ease;
}
.profile-card .menu-list .menu-item .menu-icon {
  min-width: 40px;
  margin-right: 8px;
}
.profile-card .menu-list .menu-item .menu-icon .v-icon {
  color: #757575;
  font-size: 20px;
}
.profile-card .menu-list .menu-item .menu-title {
  font-size: 14px;
  font-weight: 500;
  color: #212121;
}
.profile-card .menu-list .menu-item:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
}
.profile-card .menu-list .menu-item:hover .menu-icon .v-icon {
  color: #1976D2;
}
.profile-card .menu-list .menu-item:hover .menu-title {
  color: #1976D2;
}

@media (max-width: 960px) {
  .profile-menu {
    margin-left: 8px;
  }
  .profile-card .profile-header {
    padding: 0 12px 8px;
  }
  .profile-card .profile-header .header-avatar {
    width: 40px !important;
    height: 40px !important;
  }
  .profile-card .profile-header .profile-info .profile-name {
    font-size: 14px;
  }
  .profile-card .profile-header .profile-info .profile-email {
    font-size: 12px;
  }
  .profile-card .menu-list .menu-item {
    padding: 8px 12px;
    min-height: 44px;
  }
  .profile-card .menu-list .menu-item .menu-icon {
    min-width: 36px;
  }
  .profile-card .menu-list .menu-item .menu-icon .v-icon {
    font-size: 18px;
  }
  .profile-card .menu-list .menu-item .menu-title {
    font-size: 13px;
  }
}

/* Profile button active state */
.profile-btn.profile-active .profile-avatar {
  border-color: #2BB066 !important;
  box-shadow: 0 0 0 2px rgba(43, 176, 102, 0.3);
}

.profile-avatar.avatar-active {
  border-color: #2BB066 !important;
  box-shadow: 0 0 0 2px rgba(43, 176, 102, 0.3);
}

/* Desktop profile menu active states */
.menu-item.menu-item-active {
  background-color: rgba(43, 176, 102, 0.15) !important;
}

.menu-title.menu-title-active {
  color: #1976D2 !important;
  font-weight: 600;
}

/* Mobile navigation active states */
.mobile-drawer .nav-items .nav-item.active-item {
  background-color: rgba(43, 176, 102, 0.15) !important;
  border-left: 4px solid #2BB066;
}

.mobile-drawer .nav-items .nav-item.active-item .item-title {
  color: #1976D2 !important;
  font-weight: 600;
}

.mobile-drawer .nav-items .nav-item.active-item .v-icon {
  color: #1976D2 !important;
}

/* Mobile profile options active states */
.mobile-drawer .action-section .profile-options .profile-item.profile-item-active {
  background-color: rgba(43, 176, 102, 0.15) !important;
}

.mobile-drawer .action-section .profile-options .profile-item .profile-title-active {
  color: #1976D2 !important;
  font-weight: 600;
}
</style>